<template>
    <div class="preview flex w-full flex-col items-center gap-4">
        <!-- Remounting the stage restarts every CSS animation inside it, which is how "Replay" works.
             The two states share one grid cell, so the stage keeps a steady height across the merge. -->
        <div :key="runId" class="grid w-full justify-items-center">
            <div class="people col-start-1 row-start-1 flex w-full items-start justify-center gap-6 sm:gap-10" aria-hidden="true">
                <div class="person person--left flex w-full max-w-75 flex-col gap-5 rounded-2xl border border-card-cream-border bg-surface-raised p-6">
                    <div class="flex items-center gap-3">
                        <span class="swatch-colleague size-8.5 shrink-0 rounded-full"></span>
                        <span class="display-small-serif text-black">{{ colleagueName }}</span>
                    </div>
                    <div class="flex flex-col items-start gap-2">
                        <span v-for="(trait, index) in colleagueTraits" :key="index" class="trait trait-colleague" :style="{ '--j': index }">{{ trait }}</span>
                    </div>
                </div>
                <div class="person person--right flex w-full max-w-75 flex-col gap-5 rounded-2xl border border-card-cream-border bg-surface-raised p-6">
                    <div class="flex items-center gap-3">
                        <span class="swatch-you size-8.5 shrink-0 rounded-full"></span>
                        <span class="display-small-serif text-black">{{ t("connections.collaborationProfilePreview.you") }}</span>
                    </div>
                    <div class="flex flex-col items-start gap-2">
                        <span v-for="(trait, index) in yourTraits" :key="index" class="trait trait-you" :style="{ '--j': index }">{{ trait }}</span>
                    </div>
                </div>
            </div>

            <div class="combined col-start-1 row-start-1 flex flex-col gap-6 self-center rounded-lgr bg-surface-raised px-10 py-6 shadow-md">
                <div class="flex items-center gap-3">
                    <svg width="52" height="34" viewBox="0 0 52 34" fill="none" aria-hidden="true">
                        <defs>
                            <clipPath :id="overlapClipId"><circle cx="17" cy="17" r="17" /></clipPath>
                        </defs>
                        <circle cx="17" cy="17" r="17" class="fill-you" />
                        <circle cx="35" cy="17" r="17" class="fill-colleague" />
                        <circle cx="35" cy="17" r="17" class="fill-overlap" :clip-path="`url(#${overlapClipId})`" />
                    </svg>
                    <span class="display-small-serif text-ink">{{ t("connections.collaborationProfilePreview.combinedTitle", { name: colleagueName }) }}</span>
                </div>
                <div class="flex flex-col items-start gap-4">
                    <span v-for="(tip, index) in tips" :key="index" class="scaffold-body-small inline-flex items-center gap-3 text-ink-muted">
                        <component :is="tip.icon" class="glyph size-4 shrink-0" />
                        {{ tip.text }}
                    </span>
                </div>
            </div>
        </div>

        <BaseButton theme="tertiary" size="sm" class="replay gap-2" @click="runId += 1">
            <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
            {{ t("connections.collaborationProfilePreview.replay") }}
        </BaseButton>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { COLLAB_ICONS } from "~vue/utils/collabIcons";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

// Illustrative stand-in content: the preview runs before the user has added anyone, so there is no
// real person or profile to draw on.
const { t } = useI18n();
const colleagueName = computed(() => t("connections.collaborationProfilePreview.colleagueName"));
const colleagueTraits = computed(() => [
    t("connections.collaborationProfilePreview.colleagueTraits.directFeedback"),
    t("connections.collaborationProfilePreview.colleagueTraits.bigPictureFirst"),
    t("connections.collaborationProfilePreview.colleagueTraits.morningFocus"),
    t("connections.collaborationProfilePreview.colleagueTraits.asyncFriendly"),
]);
const yourTraits = computed(() => [
    t("connections.collaborationProfilePreview.yourTraits.detailOriented"),
    t("connections.collaborationProfilePreview.yourTraits.deepWorkBlocks"),
    t("connections.collaborationProfilePreview.yourTraits.prefersWritten"),
    t("connections.collaborationProfilePreview.yourTraits.quickToReply"),
]);
const tips = computed(() => [
    { icon: COLLAB_ICONS.triangle.component, text: t("connections.collaborationProfilePreview.tips.bigPictureFirst") },
    { icon: COLLAB_ICONS.asterisk.component, text: t("connections.collaborationProfilePreview.tips.morningReviews") },
    { icon: COLLAB_ICONS.circle.component, text: t("connections.collaborationProfilePreview.tips.writtenBrief") },
    { icon: COLLAB_ICONS.star.component, text: t("connections.collaborationProfilePreview.tips.succinct") },
]);

const overlapClipId = useId();
const runId = ref(0);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* Designer-specified tints, one per person, so the two cards read as two people. BaseChip has no
       theme for either and these are illustration decoration rather than status chips. */
    .trait {
        @apply rounded-full px-3 py-1 scaffold-details-small whitespace-nowrap;
    }

    .trait-colleague {
        background: #f7dbe8;
        color: #a11e5c;
    }

    .trait-you {
        background: #d5f2f9;
        color: #0e7c94;
    }

    .swatch-colleague {
        background: var(--color-collaboration-profile-colleague);
    }

    .swatch-you {
        background: var(--color-collaboration-profile-you);
    }

    .fill-colleague {
        fill: var(--color-collaboration-profile-colleague);
    }

    .fill-you {
        fill: var(--color-collaboration-profile-you);
    }

    .fill-overlap {
        fill: var(--blend);
    }

    .glyph {
        color: var(--blend);
    }

    /* One timeline, so a change to the tag stagger doesn't have to be propagated by hand through
       every downstream delay. The combined card starts before the cards finish leaving, which makes
       the handover a crossfade with no empty frame. */
    .preview {
        --tags-at: 400ms;
        --tag-step: 90ms;
        --merge-out-at: 2700ms;
        --merge-in-at: 3050ms;
        --replay-at: 3750ms;
    }

    .trait {
        opacity: 0;
        animation: trait-in 0.5s ease-out both;
        animation-delay: calc(var(--tags-at) + var(--j, 0) * var(--tag-step));
    }

    /* Once both sets of traits have landed the cards slide toward each other and fade, so the
       combined card that replaces them reads as their union rather than as a third thing. */
    .person {
        box-shadow: 4px 5px 12px rgb(0 0 0 / 8%);
        animation: merge-out 0.6s cubic-bezier(0.4, 0, 0.2, 1) both;
        animation-delay: var(--merge-out-at);
    }

    .person--left {
        --merge-x: 90px;
    }

    .person--right {
        --merge-x: -90px;
    }

    .combined {
        /* Designer-specified blend of the two people's colours, used by everything the combined
           profile draws. */
        --blend: #78286e;

        opacity: 0;
        animation: combined-in 0.6s cubic-bezier(0.22, 0.61, 0.36, 1) both;
        animation-delay: var(--merge-in-at);
    }

    /* Hidden rather than merely transparent, so the control can't be clicked before it appears. */
    .replay {
        animation: replay-in 0.4s ease-out both;
        animation-delay: var(--replay-at);
    }
}

@keyframes trait-in {
    0% {
        opacity: 0;
        transform: translateY(0.5rem) scale(0.96);
    }

    100% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes merge-out {
    0% {
        opacity: 1;
        transform: translateX(0) scale(1);
    }

    100% {
        opacity: 0;
        transform: translateX(var(--merge-x, 0)) scale(0.96);
    }
}

@keyframes combined-in {
    0% {
        opacity: 0;
        transform: scale(0.96);
    }

    100% {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes replay-in {
    0% {
        opacity: 0;
        visibility: hidden;
    }

    100% {
        opacity: 1;
        visibility: visible;
    }
}

/* Two cards side by side need room the small breakpoint doesn't have, and without motion the merge
   can't be told at all. Both cases skip straight to the outcome the merge was building toward. */
@media (max-width: 40rem), (prefers-reduced-motion: reduce) {
    .people {
        display: none;
    }

    .combined {
        animation: none;
        opacity: 1;
    }

    .replay {
        animation: none;
    }
}

@media (max-width: 40rem) {
    .replay {
        display: none;
    }
}
</style>
